/**
 * Os campos personalizados de um registro (#568): para ler e para preencher.
 *
 * `ValoresDosCampos` mostra o que está preenchido, na ordem do catálogo; o
 * campo arquivado só aparece se tiver valor, para o que foi preenchido não
 * sumir. `CamposEditaveis` é o bloco de entradas de um formulário: quem o usa
 * guarda a mudança e manda só ela ao salvar.
 */

import type { ReactNode } from 'react';
import type {
  CampoPersonalizado,
  MudancaDeCampos,
  ValorDeCampo,
  ValoresDeCampos,
} from '@cliqcrm/contracts';
import { camposVivos, textoDoValor, valorAtual } from '../lib/valor';
import { EntradaDoCampo } from './entrada-do-campo';
import './campos-personalizados.css';

export function ValoresDosCampos({
  campos,
  valores,
}: {
  campos: readonly CampoPersonalizado[];
  valores: ValoresDeCampos;
}): ReactNode {
  const visiveis = [...campos]
    .sort((a, b) => a.ordem - b.ordem)
    .filter((campo) => !campo.arquivado || valores[campo.id] !== undefined);
  if (visiveis.length === 0) return null;
  return (
    <dl className="campos-reg">
      {visiveis.map((campo) => {
        const texto = textoDoValor(campo, valores[campo.id]);
        return (
          <div key={campo.id} className="campos-reg__linha">
            <dt className="campos-reg__nome">
              {campo.nome}
              {campo.arquivado && <span className="campos-reg__arquivado"> (arquivado)</span>}
            </dt>
            <dd className={texto ? 'campos-reg__valor' : 'campos-reg__valor campos-reg__vazio'}>
              {texto ?? 'Não preenchido'}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

export function CamposEditaveis({
  campos,
  valores,
  mudanca,
  aoMudar,
  id,
  apenas,
}: {
  campos: readonly CampoPersonalizado[];
  valores: ValoresDeCampos;
  mudanca: MudancaDeCampos;
  aoMudar: (mudanca: MudancaDeCampos) => void;
  /** Prefixo dos ids dos controles, único na tela. */
  id: string;
  /** Só estes campos (os que a recusa do servidor apontou, por exemplo). */
  apenas?: readonly string[];
}): ReactNode {
  const mostrados = camposVivos(campos).filter(
    (campo) => apenas === undefined || apenas.includes(campo.id),
  );
  if (mostrados.length === 0) return null;
  return (
    <div className="campos-reg__formulario">
      {mostrados.map((campo) => (
        <EntradaDoCampo
          key={campo.id}
          id={id}
          campo={campo}
          valor={valorAtual(campo.id, valores, mudanca)}
          aoMudar={(valor: ValorDeCampo | null) => aoMudar({ ...mudanca, [campo.id]: valor })}
        />
      ))}
    </div>
  );
}
